import { ReactNode, useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import {
  PostOfficeEnvironmentConfig,
  PostOfficeProvider as PostOfficeBaseProvider,
} from '@atlassian/post-office-placement-remote';

import { useAppData } from 'src/app/data';
import {
  getIsWorkspaceAdmin,
  getWorkspaceOrgId,
} from 'src/selectors/global-selectors';
import { getCurrentWorkspaceExtraAttributes } from 'src/selectors/workspace-selectors';
import { analyticsClient } from 'src/utils/analytics/client';
import { BbEnv, getBbEnv } from 'src/utils/bb-env';

export const transformDeployEnvToPostOfficeEnvironment = (
  env: BbEnv
): PostOfficeEnvironmentConfig['env'] => {
  if (env === BbEnv.Test) {
    return 'test';
  } else if (env === BbEnv.Development) {
    return 'dev';
  } else if (env === BbEnv.Staging || env === BbEnv.Integration) {
    return 'staging';
  }

  return 'production';
};

type PostOfficeContextProviderProps = { children: ReactNode };

/*
 * This component is used to provide the PostOfficeContext to the application. It provides the product, subproduct and locale at the application level.
 */
const PostOfficeContextProviderImpl = ({
  children,
}: PostOfficeContextProviderProps): JSX.Element => {
  const { locale } = useIntl();
  const { tenantId } = useAppData();
  const env = getBbEnv();
  const workspaceOrgId = useSelector(getWorkspaceOrgId);
  const isWorkspaceAdmin = useSelector(getIsWorkspaceAdmin);
  const { hasPremium, isPaid, hasDefaultFree } = useSelector(
    getCurrentWorkspaceExtraAttributes
  );

  const analyticsClientInstance = analyticsClient();

  const workspaceAri = tenantId
    ? `ari:cloud:bitbucket::workspace/${tenantId}`
    : 'no-workspace-ari-available'; // Bitbucket tenantId is the equivalent of Atlassian workspaceId

  const workspaceId = tenantId || undefined;

  const productEdition = (() => {
    if (hasPremium) {
      return 'PREMIUM';
    }
    if (isPaid) {
      return 'STANDARD';
    }
    if (hasDefaultFree) {
      return 'FREE';
    }
    return undefined;
  })();

  const context = useMemo(
    () => ({
      current: {
        product: 'bitbucket',
        locale,
        workspaceAri,
        isSiteAdmin: isWorkspaceAdmin, // Bitbucket is tenantless, but we'll treat isSiteAdmin as isWorkspaceAdmin
        ...(workspaceId !== undefined ? { workspaceId } : {}),
        ...(workspaceOrgId !== undefined ? { orgId: workspaceOrgId } : {}),
        ...(productEdition !== undefined ? { productEdition } : {}),
      },
    }),
    [
      locale,
      workspaceAri,
      isWorkspaceAdmin,
      workspaceId,
      workspaceOrgId,
      productEdition,
    ]
  );

  const environment = useMemo(
    () => ({
      env: transformDeployEnvToPostOfficeEnvironment(env),
    }),
    [env]
  );

  return (
    <PostOfficeBaseProvider
      context={context}
      environment={environment}
      analyticsClientInstance={analyticsClientInstance}
    >
      {children}
    </PostOfficeBaseProvider>
  );
};

export const PostOfficeContextProvider = PostOfficeContextProviderImpl;
